/**
 * The days a task can be pushed to from the today panel's «Перенести» menu.
 *
 * These are **calendar** days, not the 03:00-rollover day `lib/day.ts` uses for
 * anything self-reported: a Todoist due date is a date, and at 01:00 a task
 * moved "to tomorrow" has to land on the date tomorrow's calendar shows, not on
 * the one the evening still feels like.
 *
 * The two non-obvious ones follow `routes_notes.py::_task_due_date`, so a note
 * sent to Todoist and a task moved here land on the same day:
 *
 * * **week** — the Sunday that closes the current Mon–Sun week (today, when
 *   today already is Sunday).
 * * **month** — the last calendar day of the current month.
 */

import { shiftIsoDay } from "./day";
import { dayLabel } from "./agenda";

/** Local calendar day of a `Date` as ISO `YYYY-MM-DD` (the `sv` locale is that format). */
function isoDay(at: Date): string {
  return at.toLocaleDateString("sv");
}

export interface TaskHorizons {
  today: string;
  tomorrow: string;
  /** Sunday of the current Mon–Sun week; `today` when today is Sunday. */
  week: string;
  /** Last calendar day of the current month; `today` when today is that day. */
  month: string;
}

/** The four days «Перенести» offers, from an ISO calendar day. */
export function taskHorizons(today: string): TaskHorizons {
  const at = new Date(`${today}T00:00:00`);
  if (Number.isNaN(at.getTime())) {
    return { today, tomorrow: today, week: today, month: today };
  }
  // `getDay()` is 0 on Sunday, which in a Mon–Sun week is the *last* day —
  // hence the special case rather than `6 - weekday`.
  const weekday = at.getDay();
  return {
    today,
    tomorrow: shiftIsoDay(today, 1),
    week: shiftIsoDay(today, weekday === 0 ? 0 : 7 - weekday),
    // Day 0 of next month is the last day of this one, leap Februaries included.
    month: isoDay(new Date(at.getFullYear(), at.getMonth() + 1, 0)),
  };
}

export interface MoveTarget {
  /** Menu label — «На завтра», «На цей тиждень». */
  label: string;
  day: string;
}

/** Every horizon «Перенести» knows, shortest first. */
const HORIZONS: { label: string; of: keyof TaskHorizons }[] = [
  { label: "На сьогодні", of: "today" },
  { label: "На завтра", of: "tomorrow" },
  { label: "На цей тиждень", of: "week" },
  { label: "На цей місяць", of: "month" },
];

/**
 * The «Перенести» choices for a task currently due on `from`.
 *
 * Two things are dropped, for the same reason — a menu row that changes nothing
 * is a menu row that lies:
 *
 * * the day the task is **already** on (which is what hides «На сьогодні» for
 *   everything except an overdue task, with no special case for it), and
 * * a horizon that resolves to a day an earlier one already offered. On a
 *   Saturday «На завтра» and «На цей тиждень» are both Sunday; on the last
 *   Sunday of a month, «На цей тиждень» and «На цей місяць» are the same date
 *   too. The shorter horizon wins: it is the one that says what it means.
 */
export function moveTargets(today: string, from: string | null): MoveTarget[] {
  const days = taskHorizons(today);
  const taken = new Set(from ? [from] : []);
  const out: MoveTarget[] = [];
  for (const horizon of HORIZONS) {
    const day = days[horizon.of];
    if (taken.has(day)) continue;
    taken.add(day);
    out.push({ label: horizon.label, day });
  }
  return out;
}

/** `dayLabel`'s relative words, which read as a sentence tail in lower case. */
const RELATIVE = new Set(["Сьогодні", "Завтра", "Вчора"]);

/**
 * The tail of «Перенесено на …»: «сьогодні», «завтра», or «Нд, 20 вересня».
 *
 * A concrete date rather than «на цей тиждень», because the menu label says
 * which horizon was chosen and the toast has to say which *day* that turned out
 * to be — on a Wednesday «на цей тиждень» alone tells you nothing you can undo.
 */
export function moveTargetLabel(day: string, today: string): string {
  const label = dayLabel(day, today);
  return RELATIVE.has(label) ? label.toLocaleLowerCase("uk") : label;
}
